import { Capacitor } from "@capacitor/core";

/**
 * Native chrome sync — keeps the iOS status bar AND the WKWebView base
 * background in lockstep with the JS theme. Solves the "light bar at top
 * (under the notch) and bottom (under the home indicator)" complaint:
 * those strips are NOT inside the WebView's drawable area; they reveal
 * the native UIView background. We sync that colour every time the
 * theme flips so the strips never break the illusion.
 *
 * Called from:
 *   - App.js useEffect on cold boot (with the persisted theme)
 *   - ThemeContext effect on every theme change
 */
function _bgHexForTheme(isDark) {
  // Match index.css palette EXACTLY:
  //   light: hsl(220, 16%, 97%)  →  ~#F5F6F8
  //   dark:  hsl(215, 14%, 11%)  →  ~#181C21
  return isDark ? "#181C21" : "#F5F6F8";
}

function _isDarkNow() {
  try { return document.documentElement.classList.contains("dark"); }
  catch { return false; }
}

export async function initStatusBar() {
  if (!Capacitor.isNativePlatform()) return;
  await syncNativeChromeToTheme();
}

/**
 * Public sync function — call after the theme flips. Idempotent and
 * try/catched per call so a single plugin glitch can't crash the
 * cascade (status bar + bg + overlay all settle independently).
 */
export async function syncNativeChromeToTheme() {
  if (!Capacitor.isNativePlatform()) return;
  const isDark = _isDarkNow();
  const bg = _bgHexForTheme(isDark);
  let StatusBar, Style;
  try {
    ({ StatusBar, Style } = await import("@capacitor/status-bar"));
  } catch (e) {
    console.warn("[Kinly] StatusBar plugin import failed:", e?.message || e);
    return;
  }
  // Each call is independently try/catched so a single API hiccup doesn't
  // abort the rest of the cascade. `setOverlaysWebView({overlay:true})` is
  // the canonical fix for the "plain bars top + bottom" complaint — it
  // lets the WebView draw under the status bar, then CSS env(safe-area-*)
  // padding in AppShell pushes content clear of the notch + home indicator
  // while preserving the themed background underneath.
  try {
    // Capacitor v7 status-bar plugin has INVERTED naming vs intuition:
    //   Style.Dark  ("DARK")  = LIGHT text on DARK background (white icons)
    //   Style.Light ("LIGHT") = DARK text on LIGHT background (black icons)
    // So in dark mode we want Style.Dark (white icons), and vice-versa.
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  } catch (e) {
    console.warn("[Kinly] StatusBar.setStyle failed:", e?.message || e);
  }
  try {
    await StatusBar.setBackgroundColor({ color: bg });
  } catch (e) {
    console.warn("[Kinly] StatusBar.setBackgroundColor failed:", e?.message || e);
  }
  try {
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (e) {
    console.warn("[Kinly] StatusBar.setOverlaysWebView failed:", e?.message || e);
  }

  // Sync the iOS software keyboard appearance to the theme. Same plugin
  // pattern as StatusBar — independent try/catch so absence of the
  // Keyboard plugin in a future build won't break theme switching.
  // KeyboardStyle.Dark = dark keyboard with light keys; .Light = white.
  try {
    const { Keyboard, KeyboardStyle } = await import("@capacitor/keyboard");
    await Keyboard.setStyle({ style: isDark ? KeyboardStyle.Dark : KeyboardStyle.Light });
  } catch (e) {
    console.warn("[Kinly] Keyboard.setStyle failed:", e?.message || e);
  }
}
